import React from 'react';
import { useDashboard } from '../contexts/DashboardContext';
import { DollarSign, TrendingUp, Target, Award, BarChart3, Scale, TrendingDown } from 'lucide-react';
import KPICard from '../components/KPICard';
import LineChart from '../components/LineChart';
import PieChart from '../components/PieChart';
import BarChart from '../components/BarChart';
import EditableText from '../components/EditableText';

const DashboardSection = () => {
  const { config, updateField } = useDashboard();

  if (!config) return <div>Loading...</div>;

  // Asset-based data for charts (no dates)
  const assetPerformanceData = [86200, 67500, 52800, 41200, 32100, 25400, 20100];
  const assetPerformanceLabels = ['BTC', 'XAU/USD', 'SPX', 'NQ', 'EUR/USD', 'XAG/USD', 'CL'];
  
  const assetAllocationData = [35, 25, 15, 12, 8, 5];
  const assetAllocationLabels = ['Bitcoin', 'Gold', 'Nasdaq', 'S&P 500', 'EUR/USD', 'Other'];
  
  const assetROIData = [18.9, 19.3, 17.8, 17.2, 16.8, 16.7, 15.2];
  const assetROILabels = ['BTC', 'XAU/USD', 'SPX', 'NQ', 'EUR/USD', 'XAG/USD', 'CL'];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-4xl font-bold text-slate-900 mb-2">
          <EditableText
            value={config.dashboard_title}
            field="dashboard_title"
            onUpdate={updateField}
            type="text"
          />
        </h2>
        <p className="text-slate-600">
          <EditableText
            value={config.overview_text}
            field="overview_text"
            onUpdate={updateField}
            type="text"
          />
        </p>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        <KPICard
          title="Total Portfolio Value"
          value={config.total_portfolio_value}
          field="total_portfolio_value"
          prefix="$"
          change={12.5}
          changeType="positive"
          icon={DollarSign}
        />
        <KPICard
          title="Daily P&L"
          value={config.daily_pnl}
          field="daily_pnl"
          prefix="$"
          change={8.3}
          changeType="positive"
          icon={TrendingUp}
        />
        <KPICard
          title="Monthly ROI"
          value={config.monthly_roi}
          field="monthly_roi"
          suffix="%"
          change={2.1}
          changeType="positive"
          icon={Target}
        />
        <KPICard
          title="Win Rate"
          value={config.win_rate}
          field="win_rate"
          suffix="%"
          change={1.5}
          changeType="positive"
          icon={Award}
        />
        <KPICard
          title="Total Trades"
          value={config.total_trades}
          field="total_trades"
          change={15}
          changeType="positive"
          icon={BarChart3}
        />
        <KPICard
          title="Risk/Reward Ratio"
          value={config.risk_reward_ratio}
          field="risk_reward_ratio"
          change={3.2}
          changeType="positive"
          icon={Scale}
        />
        <KPICard
          title="Drawdown"
          value={config.drawdown}
          field="drawdown"
          suffix="%"
          change={-2.1}
          changeType="negative"
          icon={TrendingDown}
        />
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-all duration-200 p-6">
          <p className="kpi-label mb-3">Total Holdings</p>
          <div className="kpi-number">
            <EditableText
              value={config.total_holdings}
              field="total_holdings"
              onUpdate={updateField}
              type="number"
            />
          </div>
          <p className="text-sm text-slate-600 mt-3">Active positions</p>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="lg:col-span-2">
          <LineChart
            title="Asset Performance"
            data={assetPerformanceData}
            labels={assetPerformanceLabels}
            color="#10B981"
          />
        </div>
        <PieChart
          title="Asset Allocation"
          data={assetAllocationData}
          labels={assetAllocationLabels}
        />
        <BarChart
          title="Asset ROI (%)"
          data={assetROIData}
          labels={assetROILabels}
          color="#3B82F6"
        />
      </div>

      {/* Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Daily Gainers</h3>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="daily-gainers-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-2 chart-label">Asset</th>
                  <th className="text-right py-3 px-2 chart-label">Price</th>
                  <th className="text-right py-3 px-2 chart-label">Change</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-100">
                  <td className="py-3 px-2 text-sm font-medium text-slate-900">BTC</td>
                  <td className="text-right py-3 px-2 table-number text-slate-900">$67,234</td>
                  <td className="text-right py-3 px-2 text-sm font-medium positive-value">+2.34%</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-3 px-2 text-sm font-medium text-slate-900">XAG/USD</td>
                  <td className="text-right py-3 px-2 table-number text-slate-900">$24.67</td>
                  <td className="text-right py-3 px-2 text-sm font-medium positive-value">+1.56%</td>
                </tr>
                <tr>
                  <td className="py-3 px-2 text-sm font-medium text-slate-900">CL</td>
                  <td className="text-right py-3 px-2 table-number text-slate-900">$78.45</td>
                  <td className="text-right py-3 px-2 text-sm font-medium positive-value">+1.23%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Daily Losers</h3>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="daily-losers-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-2 chart-label">Asset</th>
                  <th className="text-right py-3 px-2 chart-label">Price</th>
                  <th className="text-right py-3 px-2 chart-label">Change</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-100">
                  <td className="py-3 px-2 text-sm font-medium text-slate-900">XAU/USD</td>
                  <td className="text-right py-3 px-2 table-number text-slate-900">$2,043</td>
                  <td className="text-right py-3 px-2 text-sm font-medium negative-value">-0.45%</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-3 px-2 text-sm font-medium text-slate-900">EUR/USD</td>
                  <td className="text-right py-3 px-2 table-number text-slate-900">$1.0842</td>
                  <td className="text-right py-3 px-2 text-sm font-medium negative-value">-0.12%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardSection;